/* ==========================================================================
   Zaverni — Apple-style brand home motion engine (namespace: zap)
   Scroll source agnostic: Tinker scrolls inside .page-wrapper (>=990px),
   not the window. Everything below reads from whichever container scrolls.
   Idempotent: safe to include once per section.
   ========================================================================== */
(function () {
  if (window.__zapAppleBooted) return;
  window.__zapAppleBooted = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var rafId = null;

  function findScroller() {
    var pw = document.querySelector('.page-wrapper');
    if (pw) {
      var cs = getComputedStyle(pw);
      if (/(auto|scroll)/.test(cs.overflowY) && pw.scrollHeight > pw.clientHeight + 4) return pw;
    }
    return window;
  }

  function scrollTop() {
    var s = findScroller();
    return s === window ? window.pageYOffset || document.documentElement.scrollTop || 0 : s.scrollTop;
  }

  function scrollMax() {
    var s = findScroller();
    if (s === window) {
      return document.documentElement.scrollHeight - window.innerHeight;
    }
    return s.scrollHeight - s.clientHeight;
  }

  function vh() {
    return window.innerHeight || document.documentElement.clientHeight;
  }

  function clamp(n, a, b) {
    return n < a ? a : n > b ? b : n;
  }

  /* header height feeds .zap-showcase__sticky top offset.
     Reads how much of the header is actually on screen (it may scroll away or
     slide out on scroll-down), so the pinned section never leaves a gap. */
  var lastHH = -1;
  function syncHeaderHeight() {
    var hd = document.getElementById('header-group') || document.querySelector('#header-component') || document.querySelector('header');
    var visible = 0;
    if (hd) {
      var r = hd.getBoundingClientRect();
      visible = Math.max(0, Math.min(Math.round(hd.offsetHeight || r.height || 0), Math.round(r.bottom)));
    }
    if (visible !== lastHH) {
      lastHH = visible;
      document.documentElement.style.setProperty('--zap-hh', visible + 'px');
    }
  }

  /* ---------- elements ---------- */
  var progress = document.querySelector('[data-zap-progress]');
  var heroWrap = document.querySelector('[data-zap-hero]');
  var heroTitle = document.querySelector('[data-zap-hero-title]');
  var montage = document.querySelector('[data-zap-montage]');
  var showcase = document.querySelector('[data-zap-showcase]');
  var track = document.querySelector('[data-zap-track]');
  var dots = [].slice.call(document.querySelectorAll('[data-zap-dots] .zap-dot'));
  var idxEl = document.querySelector('[data-zap-idx]');
  var story = document.querySelector('[data-zap-story]');
  var storyBg = story ? story.querySelector('.zap-story__bg') : null;

  var pinned = false;
  var maxShift = 0;

  function measure() {
    syncHeaderHeight();
    pinned = !!showcase && window.innerWidth > 989 && !reduce;
    if (pinned) {
      showcase.style.height = Math.round(vh() * 5.2) + 'px';
      maxShift = Math.max(0, track.scrollWidth - window.innerWidth + 48);
    } else if (showcase) {
      showcase.style.height = '';
      if (track) track.style.transform = 'none';
    }
  }

  function frame() {
    rafId = null;
    syncHeaderHeight();
    var y = scrollTop();

    /* progress */
    if (progress) {
      var max = scrollMax();
      progress.style.width = (max > 0 ? clamp(y / max, 0, 1) * 100 : 0) + '%';
    }

    if (!reduce) {
      /* hero: title drifts down + fades */
      if (heroWrap && heroTitle) {
        var hr = heroWrap.getBoundingClientRect();
        var passed = clamp(-hr.top, 0, 620);
        heroTitle.style.transform = 'translate3d(0,' + passed * 0.16 + 'px,0)';
        heroTitle.style.opacity = String(1 - (passed / 620) * 0.85);
      }
      /* montage cards: layered parallax */
      if (montage) {
        var mr = montage.getBoundingClientRect();
        if (mr.bottom > 0 && mr.top < vh()) {
          var cards = montage.querySelectorAll('[data-speed]');
          for (var i = 0; i < cards.length; i++) {
            var sp = parseFloat(cards[i].getAttribute('data-speed')) || 0.08;
            cards[i].style.transform = 'translate3d(0,' + -clamp(-mr.top, 0, 900) * sp + 'px,0)';
          }
        }
      }
      /* story background parallax */
      if (story && storyBg) {
        var sr = story.getBoundingClientRect();
        if (sr.bottom > -200 && sr.top < vh() + 200) {
          var mid = sr.top + sr.height / 2 - vh() / 2;
          storyBg.style.transform = 'translate3d(0,' + clamp(mid * 0.12, -90, 90) + 'px,0)';
        }
      }
    }

    /* pinned horizontal showcase — rect based, works in any scroll container */
    if (pinned && showcase && track) {
      var wr = showcase.getBoundingClientRect();
      var total = showcase.offsetHeight - vh();
      var p = clamp(-wr.top / (total || 1), 0, 1);
      track.style.transform = 'translate3d(' + -p * maxShift + 'px,0,0)';
      if (dots.length) {
        var n = dots.length;
        var active = Math.min(n - 1, Math.round(p * (n - 1)));
        for (var k = 0; k < n; k++) dots[k].classList.toggle('is-on', k === active);
        if (idxEl) idxEl.textContent = ('0' + (active + 1)).slice(-2);
      }
    }
  }

  function onScroll() {
    if (rafId === null) rafId = window.requestAnimationFrame(frame);
  }

  /* ---------- reveal + count-up ---------- */
  function initObservers() {
    var els = document.querySelectorAll('.zap-reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) {
          en.target.classList.add('is-in');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.08 });
    els.forEach(function (e) { io.observe(e); });

    var nums = document.querySelectorAll('[data-zap-count]');
    if (reduce) {
      nums.forEach(function (el) { el.textContent = el.getAttribute('data-zap-count') + (el.getAttribute('data-zap-suffix') || ''); });
      return;
    }
    var io2 = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        io2.unobserve(el);
        var target = parseFloat(el.getAttribute('data-zap-count')) || 0;
        var dec = parseInt(el.getAttribute('data-zap-dec') || '0', 10);
        var suf = el.getAttribute('data-zap-suffix') || '';
        var start = null;
        var dur = 1100;
        function step(ts) {
          if (!start) start = ts;
          var t = Math.min(1, (ts - start) / dur);
          var e = 1 - Math.pow(1 - t, 3);
          var v = target * e;
          el.textContent = (dec ? v.toFixed(dec) : Math.round(v)) + suf;
          if (t < 1) window.requestAnimationFrame(step);
        }
        window.requestAnimationFrame(step);
      });
    }, { threshold: 0.4 });
    nums.forEach(function (el) { io2.observe(el); });
  }

  function boot() {
    measure();
    frame();
    initObservers();
  }

  /* ---------- listeners ---------- */
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  var sc = findScroller();
  if (sc !== window) sc.addEventListener('scroll', onScroll, { passive: true });

  window.addEventListener('resize', function () { measure(); frame(); });
  window.addEventListener('load', function () { measure(); frame(); });

  document.addEventListener('shopify:section:load', function () { measure(); frame(); });
  document.addEventListener('shopify:section:select', function () { measure(); frame(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* late re-measure: fonts / images shift layout */
  setTimeout(function () { measure(); frame(); }, 600);
})();
